import {FC, useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';
//mui
import {Box, Tab, Tabs, Typography} from '@mui/material';
//formik
import {Formik} from 'formik';
import {AttendeeDetailsFormValues, initialValues, validationSchema} from './formConfiguration';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {clearAttendee, getAttendee, getAttendeeSelector, getAttendeesStatistics} from '@/store/organization-attendees';
import {getCurrentEventSelector} from '@/store/organization-event';
//services
import {OrganizationAttendeesService} from '@/utils/services';
//helpers
import {ATTENDEE_DETAILS_TABS, attendeeDetailsTabs, getAttendeeInitialValues} from './helpers';
import {getErrorMessage} from '@/utils/helpers';
//constants
import {BODY1, H5, HASH} from '@/utils/constants';
//components
import FormContent from './FormContent';
import AttendeeBidsDetailsTab from './AttendeeBidsDetailsTab';
import AttendeeContributionTabDetails from './AttendeeContributionTabDetails';
//styles
import {attendeeDetailsStyle} from './AttendeeDetails.styles';

interface AttendeeDetailsProps {
	onCloseModal?: () => void;
	handleProfileTabStyles: (isProfilePage: boolean) => void;
	activeAttendeeTab?: string;
	isTabsVisible?: boolean;
}

const AttendeeDetails: FC<AttendeeDetailsProps> = ({
	onCloseModal,
	handleProfileTabStyles,
	activeAttendeeTab: initialActiveTab,
	isTabsVisible = true,
}) => {
	const dispatch = useAppDispatch();

	const attendee = useAppSelector(getAttendeeSelector);
	const currentEvent = useAppSelector(getCurrentEventSelector);

	const [activeAttendeeTab, setActiveAttendeeTab] = useState<string>(initialActiveTab || attendeeDetailsTabs[0]);

	const changeAttendeeTab = (newTab: any) => () => setActiveAttendeeTab(newTab);

	useEffect(() => {
		return () => {
			dispatch(clearAttendee());
		};
	}, [dispatch]);

	const handleSubmitClickHandler = async (values: AttendeeDetailsFormValues) => {
		if (!currentEvent || !attendee) return;
		try {
			const {id, ...reqBody} = values;
			await OrganizationAttendeesService.updateAttendee(id, reqBody);
			await dispatch(getAttendee(attendee?.id)).unwrap();
			toast.success('Attendee information updated successfully');
			await dispatch(getAttendeesStatistics(currentEvent.id)).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const formikInitialValues = useMemo(() => {
		if (attendee) {
			const attendeeInitialValues = getAttendeeInitialValues(attendee);
			return attendeeInitialValues;
		}
		return initialValues;
	}, [attendee]);

	const attendeeTabData = useMemo(() => {
	handleProfileTabStyles(false);

	switch (activeAttendeeTab) {
		case ATTENDEE_DETAILS_TABS.PROFILE:
			handleProfileTabStyles(true);
			return (
				<Formik enableReinitialize initialValues={formikInitialValues} validationSchema={validationSchema} onSubmit={handleSubmitClickHandler}>
					<FormContent />
				</Formik>
			);
		case ATTENDEE_DETAILS_TABS.BIDS:
			return <AttendeeBidsDetailsTab />;
		case ATTENDEE_DETAILS_TABS.CONTRIBUTIONS:
			return <AttendeeContributionTabDetails />;
		default:
			return null;
	}
}, [activeAttendeeTab, formikInitialValues, handleSubmitClickHandler, handleProfileTabStyles]);

	return (
		<Box sx={attendeeDetailsStyle.wrapper}>
			<Box sx={attendeeDetailsStyle.content}>
				<Typography variant={H5}>
					{attendee?.userInfo?.firstName} {attendee?.userInfo?.lastName}
				</Typography>
				<Typography variant={BODY1}>
					{HASH} {attendee?.id}
				</Typography>
			</Box>

		{isTabsVisible && (
				<Tabs value={activeAttendeeTab} sx={attendeeDetailsStyle.tabs}>
				{attendeeDetailsTabs.map((tab: string) => (
					<Tab
						onClick={changeAttendeeTab(tab)}
						key={tab}
						value={tab}
						label={<Box>{tab}</Box>}
						sx={attendeeDetailsStyle.tab}
						disableRipple
					/>
				))}
			</Tabs>)}

			{attendeeTabData}
		</Box>
	);
};

export default AttendeeDetails;
